import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { DashboardLayout } from '../layout/DashboardLayout';
import {
  ArrowBack as ArrowBackIcon,
  Article as ArticleIcon,
  School as ConceptIcon,
  Description as DocumentIcon,
  Build as SkillIcon,
  Work as UseCaseIcon,
  Settings as ProcessIcon,
  Folder as ResourceIcon,
  Warning as RiskIcon,
  Security as ControlIcon,
  Analytics as MetricIcon,
  Person as RoleIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

// Mock data - in a real app, this would come from an API
const mockArticles = {
  'person-1': {
    id: 'person-1',
    title: 'John Smith - Senior Developer',
    type: 'Role',
    content: `
# John Smith - Senior Developer

John Smith is a Senior Developer with 8 years of experience in frontend development and modern web technologies. He joined our team in 2020 and has been instrumental in building our core platform.

## Expertise Areas

### React/TypeScript - Expert Level (95%)
John has extensive experience with React ecosystem and TypeScript. He has:
- Built complex single-page applications
- Implemented advanced state management patterns
- Created reusable component libraries
- Mentored junior developers

### AI/ML - Intermediate Level (65%)
John has been expanding his knowledge in AI/ML:
- Completed several online courses and certifications
- Implemented ML models in production applications
- Collaborates closely with our data science team
- Contributes to ML documentation and best practices

## Key Contributions

- **Documentation**: Created comprehensive guides for React/TypeScript development
- **Code Reviews**: Maintains high code quality standards across the team
- **Mentoring**: Guides junior developers and new team members
- **Architecture**: Designed scalable frontend architecture patterns

## Current Projects

- Leading the frontend development of our new analytics dashboard
- Implementing real-time data visualization components
- Building ML model integration interfaces
- Optimizing application performance and user experience

## Education & Certifications

- Bachelor's in Computer Science, Stanford University
- AWS Certified Developer Associate
- Google Cloud Professional Developer
- React Advanced Patterns Certification
    `,
    author: 'HR Team',
    lastUpdated: '2024-01-15',
    tags: ['Senior Developer', 'React', 'TypeScript', 'AI/ML'],
    relatedConcepts: ['Frontend Development', 'Machine Learning', 'Web Development'],
  },
  'person-2': {
    id: 'person-2',
    title: 'Sarah Johnson - Data Scientist',
    type: 'Role',
    content: `
# Sarah Johnson - Data Scientist

Sarah Johnson is our lead Data Scientist with a PhD in Statistics and 6 years of experience in machine learning and statistical analysis. She joined our team in 2021 and has been driving our data science initiatives.

## Expertise Areas

### Python - Expert Level (90%)
Sarah is highly proficient in Python for data science:
- Advanced pandas, numpy, and scikit-learn expertise
- Deep learning with TensorFlow and PyTorch
- Data visualization with matplotlib and seaborn
- Statistical analysis and hypothesis testing

### AI/ML - Expert Level (90%)
Sarah specializes in machine learning:
- Predictive modeling and classification
- Natural language processing
- Computer vision applications
- Model deployment and MLOps

### Data Science - Expert Level (95%)
Sarah has deep expertise in data science:
- Statistical modeling and analysis
- Experimental design and A/B testing
- Data preprocessing and feature engineering
- Business intelligence and reporting

## Key Contributions

- **Research**: Published 3 papers on predictive modeling in top-tier journals
- **Models**: Developed production ML models with 95%+ accuracy
- **Team Leadership**: Mentors junior data scientists
- **Strategy**: Shapes our data science roadmap and priorities

## Current Projects

- Building recommendation systems for our platform
- Developing fraud detection algorithms
- Creating automated reporting dashboards
- Leading data quality initiatives

## Education & Certifications

- PhD in Statistics, MIT
- Master's in Computer Science, MIT
- AWS Certified Machine Learning Specialty
- Google Cloud Professional Data Engineer
    `,
    author: 'HR Team',
    lastUpdated: '2024-01-12',
    tags: ['Data Scientist', 'Python', 'Machine Learning', 'Statistics'],
    relatedConcepts: ['Data Science', 'Machine Learning', 'Statistics'],
  },
  'concept-1': {
    id: 'concept-1',
    title: 'Machine Learning',
    type: 'Concept',
    content: `
# Machine Learning

Machine learning is a subset of artificial intelligence (AI) that enables systems to learn and improve from experience without being explicitly programmed. It focuses on the development of computer programs that can access data and use it to learn for themselves.

## Key Concepts

### Supervised Learning
Supervised learning is the machine learning task of learning a function that maps an input to an output based on example input-output pairs. It infers a function from labeled training data consisting of a set of training examples.

### Unsupervised Learning
Unsupervised learning is a type of machine learning that looks for previously undetected patterns in a data set with no pre-existing labels and with a minimum of human supervision.

### Reinforcement Learning
Reinforcement learning is an area of machine learning concerned with how software agents ought to take actions in an environment in order to maximize the notion of cumulative reward.

## Applications

- **Computer Vision**: Image recognition, object detection, facial recognition
- **Natural Language Processing**: Language translation, sentiment analysis, chatbots
- **Recommendation Systems**: Product recommendations, content personalization
- **Predictive Analytics**: Forecasting, risk assessment, fraud detection

## Best Practices

1. **Data Quality**: Ensure your training data is clean, relevant, and representative
2. **Feature Engineering**: Create meaningful features that help the model learn
3. **Model Selection**: Choose the right algorithm for your problem type
4. **Validation**: Use proper cross-validation techniques to assess performance
5. **Monitoring**: Continuously monitor model performance in production
    `,
    author: 'Dr. Jane Smith',
    lastUpdated: '2024-01-15',
    tags: ['AI', 'Data Science', 'Algorithms'],
    relatedConcepts: ['Deep Learning', 'Neural Networks', 'Data Mining'],
  },
  'document-1': {
    id: 'document-1',
    title: 'ML Best Practices Guide',
    type: 'Document',
    content: `
# ML Best Practices Guide

This comprehensive guide covers essential best practices for implementing machine learning projects in production environments.

## Project Planning

### 1. Define Clear Objectives
- Establish measurable success criteria
- Identify key performance indicators (KPIs)
- Set realistic timelines and milestones

### 2. Data Strategy
- Assess data availability and quality
- Plan data collection and preprocessing pipelines
- Establish data governance policies

## Development Process

### 1. Data Preprocessing
- Handle missing values appropriately
- Normalize and scale features
- Split data into training, validation, and test sets

### 2. Model Development
- Start with simple baseline models
- Experiment with different algorithms
- Use cross-validation for robust evaluation

### 3. Feature Engineering
- Create domain-specific features
- Use feature selection techniques
- Monitor feature importance

## Deployment Considerations

### 1. Model Serving
- Choose appropriate serving infrastructure
- Implement A/B testing capabilities
- Monitor model performance in production

### 2. Monitoring and Maintenance
- Track model drift and performance degradation
- Implement automated retraining pipelines
- Maintain model versioning and rollback capabilities

## Common Pitfalls

- Overfitting to training data
- Data leakage between training and test sets
- Ignoring business context and constraints
- Insufficient validation and testing
    `,
    author: 'Team Lead',
    lastUpdated: '2024-01-10',
    tags: ['Best Practices', 'Production', 'MLOps'],
    relatedConcepts: ['Model Deployment', 'MLOps', 'Data Engineering'],
  },
};

const getNodeIcon = (type: string) => {
  switch (type) {
    case 'Concept': return <ConceptIcon />;
    case 'Document': return <DocumentIcon />;
    case 'Skill': return <SkillIcon />;
    case 'UseCase': return <UseCaseIcon />;
    case 'Process': return <ProcessIcon />;
    case 'Resource': return <ResourceIcon />;
    case 'Risk': return <RiskIcon />;
    case 'Control': return <ControlIcon />;
    case 'Metric': return <MetricIcon />;
    case 'Role': return <RoleIcon />;
    default: return <ArticleIcon />;
  }
};

export function KnowledgeArticle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const article = id ? mockArticles[id as keyof typeof mockArticles] : null;

  if (!article) {
    return (
      <DashboardLayout title="Article Not Found">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Article Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The requested article could not be found.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/knowledge-graph')}
            >
              Back to Knowledge Graph
            </Button>
          </Paper>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={article.title}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/knowledge-graph')}
            sx={{ mb: 2 }}
          >
            Back to Knowledge Graph
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          <CardHeader
            avatar={
              <Avatar sx={{ backgroundColor: 'primary.main' }}>
                {getNodeIcon(article.type)}
              </Avatar>
            }
            title={article.title}
            subheader={`By ${article.author} • ${article.lastUpdated}`}
            action={
              <Chip
                label={article.type}
                color="primary"
                variant="outlined"
                icon={getNodeIcon(article.type)}
              />
            }
          />
          
          <CardContent>
            <Box sx={{ mb: 3 }}>
              {article.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                '& h1': { typography: 'h4', mb: 2, mt: 3 },
                '& h2': { typography: 'h5', mb: 2, mt: 3 },
                '& h3': { typography: 'h6', mb: 2, mt: 2 },
                '& p': { typography: 'body1', mb: 2, lineHeight: 1.7 },
                '& ul': { pl: 3, mb: 2 },
                '& li': { typography: 'body1', mb: 1 },
                '& code': {
                  backgroundColor: 'grey.100',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                },
                '& pre': {
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: article.content.replace(/\n/g, '<br />'),
              }}
            />

            {article.relatedConcepts && article.relatedConcepts.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Related Concepts
                </Typography>
                <List dense>
                  {article.relatedConcepts.map((concept) => (
                    <ListItem key={concept} sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <LinkIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={concept} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </CardContent>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}
